import api from '../../utils/api';
import { StoredFileMetadata, Agency } from '../shared/types';


export interface EmploymentIncomeVerificationSource {
    type: string;
    employer: string | null;
    contractType: string | null;
    startDate: string | null;
    endDate: string | null;
    grossIncome: number | null;
    incomeFrequency: string | null;
    holidayAllowanceIncluded: boolean | null;
    thirteenthMonthPayIncluded: boolean | null;
    thirtyPercentFacilityApplicable: boolean | null;
    additionalIncome: number | null;
    additionalIncomeFrequency: string | null;
    additionalIncomeComment: string | null;
    employmentContract: StoredFileMetadata | null;
    employerStatement: StoredFileMetadata | null;
    employmentPayslipOne: StoredFileMetadata | null;
    employmentPayslipTwo: StoredFileMetadata | null;
    employmentPayslipThree: StoredFileMetadata | null;
}

export interface FreelanceOrBusinessOwnershipIncomeVerificationSource {
    type: string;
    employer: string | null;
    startDate: string | null;
    chamberOfCommerceNumber: string | null;
    grossIncome: number | null;
    grossIncomeRemark: string | null;
    annualStatement: StoredFileMetadata | null;
    certifiedChamberOfCommerceExtract: StoredFileMetadata | null;
    taxReturn: StoredFileMetadata | null;
}

interface PetInfo {
    species: string;
    amount: number;
}

interface MainHomeseeker {
    name: string | null;
    email: string | null;
}

interface CoTenant {
    name: string | null;
    email: string | null;
}

interface Step {
    required: boolean;
    updatedAt: string | null;
    alerts: string[] | null;
}

interface IdVerificationStep extends Step {
    data: null | {
        status: 'succeeded' | 'pending' | 'failed' | null;
    }
}

export interface IncomeVerificationSourcesStep extends Step {
    data: null | [ EmploymentIncomeVerificationSource | FreelanceOrBusinessOwnershipIncomeVerificationSource ];
}

export interface IncomeVerificationStep extends Step {
    data: null | {
        bankStatements: StoredFileMetadata[] | null;
    }
}

export interface AdditionalInformationStep extends Step {
    data: null | {
        additionalComments: string | null;
        landlordsStatement: StoredFileMetadata | null;
        extraDocuments: StoredFileMetadata[] | null;
    }
}

export interface HouseholdCompositionStep extends Step {
    data: null | {
        householdComposition: {
            amountOfAdults: number | null;
            amountOfChildren: number | null;
            pets: PetInfo[] | null;
        },
        mainHomeseeker: MainHomeseeker | null;
        coTenants: CoTenant[] | null;
    }
}

interface OverviewScreeningSteps {
    idVerification?: IdVerificationStep;
    incomeVerificationSources?: IncomeVerificationSourcesStep;
    incomeVerification?: IncomeVerificationStep;
    additionalInformation?: AdditionalInformationStep;
    householdComposition?: HouseholdCompositionStep;
}

export interface Listing {
    street: string;
    houseNumber: string;
    postalCode: string;
    city: string;
    imageUrl: string;
    price?: number;
    surface?: number;
    rooms?: number;
    agentLogo?: string;
}

export interface ScreeningOverview {
    listing: Listing;
    agency: Agency;
    completeSteps: OverviewScreeningSteps | null;
    incompleteSteps: OverviewScreeningSteps | null;
}

const findOverview = async (homeseekerId: string, verificationRequestAuthorisationId?: string): Promise<ScreeningOverview> => {
    if (verificationRequestAuthorisationId) {
        return await api.getWithoutAuth(`api/screening/${homeseekerId}/overview/${verificationRequestAuthorisationId}`);
    }

    return await api.getWithoutAuth(`api/screening/${homeseekerId}/overview`);
}

export default findOverview;
